import {
  MeetingSessionConfiguration,
  NScaleVideoUplinkBandwidthPolicy,
  VideoCodecCapability,
  ConsoleLogger,
  VideoPriorityBasedPolicy,
  DefaultBrowserBehavior,
} from "amazon-chime-sdk-js";
import { DeviceLabels } from "amazon-chime-sdk-component-library-react";
import { MeetingMode } from "../../video.types";
import { VIDEO_QUALITY, SDK_LOG_LEVELS } from "../../video.constants";
import { HiBitrate1500SimulcastUplinkPolicy } from "./HiBitrate1500SimulcastUplinkPolicy";
import { waitForAudioVideoDidStart } from "./waitForAudioVideoDidStart";
import type { JoinInfo } from "@core/features/videoMeeting";

export type JoinChimeSessionOptions = {
  joinInfo: JoinInfo;
  enableSimulcast: boolean;
  priorityBasedPolicy?: VideoPriorityBasedPolicy;
  keepLastFrameWhenPaused: boolean;
  meetingManager: any;
  setRegion: (region: string) => void;
  setMeetingMode: (mode: MeetingMode) => void;
};

const logger = new ConsoleLogger("PollinatorVideo", SDK_LOG_LEVELS.warn);

export async function joinChimeSession({
  joinInfo,
  enableSimulcast,
  priorityBasedPolicy,
  keepLastFrameWhenPaused,
  meetingManager,
  setRegion,
  setMeetingMode,
}: JoinChimeSessionOptions): Promise<void> {
  const meetingSessionConfig = new MeetingSessionConfiguration(
    joinInfo.Meeting,
    joinInfo.Attendee
  );

  setRegion(joinInfo.Meeting.MediaRegion);

  // Configure session
  meetingSessionConfig.enableSimulcastForUnifiedPlanChromiumBasedBrowsers =
    enableSimulcast;
  if (priorityBasedPolicy) {
    meetingSessionConfig.videoDownlinkBandwidthPolicy = priorityBasedPolicy;
  }
  meetingSessionConfig.keepLastFrameWhenPaused = keepLastFrameWhenPaused;

  // Configure uplink bandwidth policy. Both branches set the policy
  // explicitly so every join has a known policy class on the wire.
  //
  // Simulcast on AND Chromium-based: subclass DefaultSimulcastUplinkPolicy
  // to raise the Hi-layer max bitrate from the SDK's hardcoded 1200 kbps to
  // 1500 kbps. VP9's rate-distortion tradeoff at 1200 kbps resolves to
  // 640×360 even with link headroom; at 1500 kbps it flips toward 1280×720.
  // See the policy file's header comment for the full diagnostic.
  //
  // Simulcast off OR non-Chromium browser: NScale single-stream. The Chime
  // SDK rejects a configured simulcast policy on non-Chromium with a thrown
  // 'Simulcast is only supported on Chromium-based browsers' error
  // (DefaultMeetingSession.js:107) — so we mirror the SDK's own
  // browserBehavior.hasChromiumWebRTC() check at this layer and fall back
  // to NScale. NScale honors setVideoMaxBandwidthKbps (called below); the
  // simulcast policy's setIdealMaxBandwidthKbps is a no-op, which is why we
  // need the subclass for the simulcast path instead of the bandwidth setter.
  const selfAttendeeId = joinInfo.Attendee?.AttendeeId || "unknown";
  const browserBehavior = new DefaultBrowserBehavior();
  const usingSimulcastPolicy = enableSimulcast && browserBehavior.hasChromiumWebRTC();
  if (usingSimulcastPolicy) {
    meetingSessionConfig.videoUplinkBandwidthPolicy =
      new HiBitrate1500SimulcastUplinkPolicy(selfAttendeeId, logger);
  } else {
    meetingSessionConfig.videoUplinkBandwidthPolicy =
      new NScaleVideoUplinkBandwidthPolicy(selfAttendeeId, true, logger);
  }

  // Join (makes the audioVideo facade available)
  try {
    await meetingManager.join(meetingSessionConfig, {
      deviceLabels: DeviceLabels.None,
      enableWebAudio: true,
      skipDeviceSelection: true,
    });
  } catch (err) {
    console.error("[joinChimeSession] meetingManager.join() failed:", err);
    throw err;
  }

  // Configure video quality and codec preferences BEFORE start() so the
  // initial negotiation uses the correct parameters (avoids renegotiation
  // flicker when the user first enables their camera)
  if (meetingManager.meetingSession?.audioVideo) {
    const av = meetingManager.meetingSession.audioVideo;

    av.chooseVideoInputQuality(
      VIDEO_QUALITY.width,
      VIDEO_QUALITY.height,
      VIDEO_QUALITY.frameRate
    );

    // setVideoMaxBandwidthKbps is honored by NScaleVideoUplinkBandwidthPolicy
    // and is a no-op on the simulcast policy (the simulcast policy's
    // setIdealMaxBandwidthKbps is empty). Fire it whenever NScale is the
    // actual policy in use — that includes both simulcast-disabled and the
    // Safari/non-Chromium fallback path where simulcast is requested but
    // can't be used.
    if (!usingSimulcastPolicy) {
      av.setVideoMaxBandwidthKbps(VIDEO_QUALITY.maxBandwidthKbps);
    }

    // Prefer VP9 (better compression at same quality) with VP8 fallback
    av.setVideoCodecSendPreferences([
      VideoCodecCapability.vp9(),
      VideoCodecCapability.vp8(),
    ]);
  }

  // meetingManager.start() is fire-and-forget across three SDK layers — it
  // returns the moment it kicks off audioVideo.start(), not when start
  // actually completes. The SDK's documented contract is: subscribe to
  // AudioVideoObserver.audioVideoDidStart to know when the WebRTC handshake
  // finished. Without this wait, the device-init that follows in
  // useJoinVideoMeeting can fire SDP renegotiations during the in-flight
  // initial handshake, causing both negotiations to stall on slower paths
  // (Safari 26 / Chrome 147 on macOS Sequoia/Tahoe). See
  // docs/superpowers/plans/2026-05-04-video-webrtc-handshake-race-fix.md.
  //
  // Observer is registered BEFORE start() so a synchronous-fire from cached
  // DTLS / loopback / future SDK optimizations cannot be missed — the
  // otherwise-typical 200-2000ms handshake makes registration order
  // irrelevant in the common case, but registering first eliminates the
  // race by construction.
  const audioVideo = meetingManager.meetingSession?.audioVideo;
  if (!audioVideo) {
    // meetingManager.join() above creates the audioVideo facade. If it's
    // null here, the SDK contract is broken; throw fail-loud so the join
    // outer catch surfaces a session error rather than silently skipping
    // the wait and re-introducing the bug.
    throw new Error(
      "audioVideo facade unavailable after meetingManager.join()",
    );
  }
  const startedHandle = waitForAudioVideoDidStart(audioVideo, 15_000);

  try {
    await meetingManager.start();
  } catch (err) {
    // cancel() resolves the pending promise silently rather than rejecting
    // (see waitForAudioVideoDidStart JSDoc) — the original `err` here is
    // the one we surface; a second rejection would just confuse the catch
    // chain in useJoinVideoMeeting.
    startedHandle.cancel();
    console.error("[joinChimeSession] meetingManager.start() failed:", err);
    throw err;
  }

  try {
    await startedHandle.promise;
  } catch (err) {
    console.error(
      "[joinChimeSession] audioVideoDidStart wait failed:",
      err,
    );
    throw err;
  }

  setMeetingMode(MeetingMode.Attendee);
}
